/**
 * A piece type's param form (D22's param specs: string with `enum` as a
 * picker, number/integer with bounds, boolean as a checkbox), and the mode —
 * play now into the on-air part, or queue as the next part. The values are
 * checked as the production will check them (`lib/playout/adlib.ts`), so a
 * bad value is shown here instead of coming back as `invalid-params`.
 */
import { type FormEvent, useState } from 'react';
import { type ParamField, parseParams } from '../../lib/playout/adlib.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { Button } from '../ui/button.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

export type AdlibParamFormProps = {
  readonly title: string;
  readonly fields: readonly ParamField[];
  readonly mode: 'now' | 'queue';
  readonly disabled: boolean;
  readonly onSubmit: (input: { readonly params: Record<string, JsonValue>; readonly mode: 'now' | 'queue' }) => void;
  readonly onCancel: () => void;
};

function Control({ field, value, onChange, error }: { readonly field: ParamField; readonly value: string | boolean; readonly onChange: (v: string | boolean) => void; readonly error: string | undefined }) {
  const id = `adlib-param-${field.name}`;
  const spec = field.spec;
  if (spec.type === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-sm" htmlFor={id}>
        <input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {field.label}
      </label>
    );
  }
  const text = typeof value === 'string' ? value : '';
  const required = spec.required && spec.default === null;
  return (
    <Field invalid={error !== undefined}>
      <Label htmlFor={id}>
        {field.label}
        {required ? ' *' : ''}
      </Label>
      {spec.type === 'string' && spec.enum !== null ? (
        <NativeSelect id={id} value={text} onChange={(e) => onChange(e.target.value)} aria-invalid={error !== undefined}>
          <option value="">–</option>
          {spec.enum.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </NativeSelect>
      ) : (
        <Input
          id={id}
          value={text}
          inputMode={spec.type === 'string' ? undefined : 'decimal'}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={error !== undefined}
          aria-describedby={`${id}-error`}
        />
      )}
      <FieldError id={`${id}-error`}>{error}</FieldError>
    </Field>
  );
}

export function AdlibParamForm({ title, fields, mode, disabled, onSubmit, onCancel }: AdlibParamFormProps) {
  const [values, setValues] = useState<Readonly<Record<string, string | boolean>>>(() => Object.fromEntries(fields.map((f) => [f.name, f.initial])));
  const [chosenMode, setMode] = useState(mode);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = parseParams(fields, values);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    onSubmit({ params: parsed.params, mode: chosenMode });
  };
  return (
    <form className="grid gap-3 rounded-md border bg-card p-3" onSubmit={submit} data-testid="adlib-param-form" aria-label={`Adlib ${title}`}>
      <p className="text-sm font-medium">{title}</p>
      {fields.map((field) => (
        <Control key={field.name} field={field} value={values[field.name] ?? field.initial} error={errors[field.name]} onChange={(v) => setValues({ ...values, [field.name]: v })} />
      ))}
      <fieldset className="flex gap-4 text-sm">
        <legend className="sr-only">When</legend>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="adlib-mode" checked={chosenMode === 'now'} onChange={() => setMode('now')} />
          Now
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" name="adlib-mode" checked={chosenMode === 'queue'} onChange={() => setMode('queue')} />
          Queue as next
        </label>
      </fieldset>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={disabled} data-testid="adlib-submit">
          {chosenMode === 'now' ? 'Play now' : 'Queue'}
        </Button>
      </div>
    </form>
  );
}
